/**
 * The team-relative view of one /event/team-schedule-get event.
 *
 * ScoreRow is deliberately not reused here: its row is two-team, has no home
 * or away perspective, and cannot draw the opponent-relative single-line row
 * the design frame wants at any prop combination. statusLabel arrives already
 * rendered in the timezone the caller sent and is never re-derived.
 */

import { todayInZone } from "../scores/calendarDay";

export type typeTeamGameStatus =
  "scheduled" | "inProgress" | "final" | "postponed" | "canceled" | "delayed";

export type typeTeamGameSide = {
  teamId: string;
  name: string;
  /** The nickname events-api sends, or null (spec 1.4, ISSUE-1197). */
  shortName?: string | null;
  abbreviation: string;
  /** The plate fill when the side has no logo. */
  primaryColorHex?: string | null;
  score?: number | null;
  /** The side's resolved logo URL (ISSUE-1087). */
  logoUrl?: string | null;
};

export type typeTeamScheduleEvent = {
  eventId: string;
  kickoffAt: string;
  status: typeTeamGameStatus;
  statusLabel: string;
  isLive: boolean;
  winnerTeamId?: string | null;
  isTie: boolean;
  home: typeTeamGameSide;
  away: typeTeamGameSide;
};

export const TEAM_HOME_PREFIX = "vs ";
export const TEAM_AWAY_PREFIX = "at ";
export const TEAM_RESULT_WIN = "W";
export const TEAM_RESULT_LOSS = "L";
export const TEAM_RESULT_TIE = "T";

/** "Nov 16" for kickoffAt in timeZone, or "" when kickoffAt does not parse. */
export function formatTeamGameDate(
  kickoffAt: string,
  timeZone: string,
): string {
  const date = new Date(kickoffAt);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
  }).format(date);
}

/** The kickoff's calendar day in `timeZone`, "YYYY-MM-DD", or "" when
 *  kickoffAt does not parse. */
export function teamGameDay(kickoffAt: string, timeZone: string): string {
  const ms = Date.parse(kickoffAt);
  return Number.isNaN(ms) ? "" : todayInZone({ nowMs: ms, timeZone });
}

export type typeTeamGameRowModel = {
  eventId: string;
  /** "vs Bengals" or "at Browns". */
  opponentLine: string;
  opponentAbbreviation: string;
  /** The opponent's logo, or null for its abbreviation plate (ISSUE-1087). */
  opponentLogoUrl: string | null;
  /** "Nov 16", or "" when kickoffAt does not parse. */
  dateLabel: string;
  /** The kickoff's calendar day in the viewer's zone, "YYYY-MM-DD", or ""
   *  when kickoffAt does not parse. Feeds the date tile and month headers. */
  day: string;
  /** The subject team plays at home: the row reads "vs", else "at". */
  isHome: boolean;
  /** The opponent's server names, for `pickTeamLabel` (spec 8). */
  opponentName: string;
  opponentShortName: string | null;
  opponentPlateColorHex: string | null;
  status: typeTeamGameStatus;
  /** The server's words, verbatim: "Final", "Sun 1:00 PM", "Q2 8:45". */
  statusLabel: string;
  isLive: boolean;
  resultLetter: "W" | "L" | "T" | null;
  /** "27-20", subject team first. "" when either score is missing. */
  scoreLabel: string;
  isPlayed: boolean;
};

export function toTeamGameRowModel(input: {
  event: typeTeamScheduleEvent;
  teamId: string;
  timeZone: string;
}): typeTeamGameRowModel {
  const { event, teamId, timeZone } = input;
  const isHome = event.home.teamId === teamId;
  // When neither side matches teamId, treat home as the subject.
  const subjectIsHome = isHome || event.away.teamId !== teamId;
  const subject = subjectIsHome ? event.home : event.away;
  const opponent = subjectIsHome ? event.away : event.home;

  const opponentLine = subjectIsHome
    ? `${TEAM_HOME_PREFIX}${opponent.name}`
    : `${TEAM_AWAY_PREFIX}${opponent.name}`;

  const isPlayed = event.status === "final";

  let resultLetter: "W" | "L" | "T" | null = null;
  if (isPlayed) {
    if (event.isTie) {
      resultLetter = TEAM_RESULT_TIE;
    } else if (
      event.winnerTeamId !== undefined &&
      event.winnerTeamId !== null
    ) {
      resultLetter =
        event.winnerTeamId === subject.teamId
          ? TEAM_RESULT_WIN
          : TEAM_RESULT_LOSS;
    }
  }

  const subjectScore = subject.score ?? null;
  const opponentScore = opponent.score ?? null;
  const scoreLabel =
    subjectScore !== null && opponentScore !== null
      ? `${subjectScore}-${opponentScore}`
      : "";

  return {
    eventId: event.eventId,
    opponentLine,
    opponentAbbreviation: opponent.abbreviation,
    opponentLogoUrl: opponent.logoUrl ?? null,
    dateLabel: formatTeamGameDate(event.kickoffAt, timeZone),
    day: teamGameDay(event.kickoffAt, timeZone),
    isHome: subjectIsHome,
    opponentName: opponent.name,
    opponentShortName: opponent.shortName ?? null,
    opponentPlateColorHex: opponent.primaryColorHex ?? null,
    status: event.status,
    statusLabel: event.statusLabel,
    isLive: event.isLive,
    resultLetter,
    scoreLabel,
    isPlayed,
  };
}
